import React, { useEffect } from 'react';
import {
  useReactTable,
  getPaginationRowModel,
  getCoreRowModel,
  Row,
  flexRender,
} from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import Pagination from '../Pagination';
import {
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableCellContent,
  StyledTableHead,
  StyledTableHeadText,
  StyledTableRow,
} from './styles';
import { TableProps } from './types';

// eslint-disable-next-line @typescript-eslint/ban-types
function Table<T extends object>({
  data,
  columns,
  onRowClick,
  pageSize = data.length,
  disabledPagination,
  pageCount,
  onPageChange,
  currentPage,
  columnVisibility,
  totalCount,
  rowSelection,
  getRowId,
  setSelectedRows,
  showPageSizeSelect,
  pageSizeSelectOptions,
  onPageSizeChange,
}: TableProps<T>): ReactComponentReturnType {
  const intl = useIntl();

  const tableInstance = useReactTable({
    columns,
    data,
    enableRowSelection: true,
    getRowId,
    initialState: {
      columnVisibility,
      pagination: { pageSize },
    },
    onRowSelectionChange: setSelectedRows,
    state: {
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: disabledPagination
      ? undefined
      : getPaginationRowModel(),
  });

  const { getRowModel, setPageSize, getHeaderGroups } = tableInstance;

  useEffect(() => {
    if (setPageSize) {
      setPageSize(pageSize);
    }
  }, [pageSize, setPageSize]);

  const dataSrc = getRowModel().rows;

  return (
    <>
      <StyledTable>
        <colgroup>
          {getHeaderGroups().map(({ headers }, i) => (
            <React.Fragment key={i as number}>
              {headers.map((header) => (
                <col
                  key={header.id}
                  style={
                    header.column.columnDef.size !== undefined
                      ? { width: header.column.columnDef.size }
                      : undefined
                  }
                />
              ))}
            </React.Fragment>
          ))}
        </colgroup>

        {/* Headers */}
        <StyledTableHead>
          {getHeaderGroups().map((headerGroup) => (
            <StyledTableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <StyledTableCell key={header.id}>
                  <StyledTableHeadText>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </StyledTableHeadText>
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </StyledTableHead>

        {/* Rows */}
        <StyledTableBody>
          {dataSrc.map((row: Row<T>, i: number) => (
            <StyledTableRow
              key={row.id}
              clickable={!!onRowClick}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <StyledTableCell key={cell.id}>
                  <StyledTableCellContent>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </StyledTableCellContent>
                </StyledTableCell>
              ))}
            </StyledTableRow>
          ))}
        </StyledTableBody>
      </StyledTable>

      {/* Pagination */}
      {!disabledPagination &&
        pageCount !== undefined &&
        onPageChange &&
        currentPage !== undefined && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderTop: '1px solid var(--line)',
            fontSize: 'var(--t-sm)',
            color: 'var(--muted)',
            flexWrap: 'wrap',
            gap: 8,
          }}>
            <div>
              {intl.formatMessage(
                {
                  id: 'table.showingRange',
                  defaultMessage: 'Showing {start}–{end} of {total}',
                },
                {
                  start: <strong style={{ color: 'var(--ink)' }}>{((currentPage - 1) * pageSize + 1).toLocaleString()}</strong>,
                  end: <strong style={{ color: 'var(--ink)' }}>{Math.min(currentPage * pageSize, totalCount ?? 0).toLocaleString()}</strong>,
                  total: (totalCount ?? 0).toLocaleString(),
                }
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {showPageSizeSelect && pageSizeSelectOptions && onPageSizeChange && (
                <>
                  <span>{intl.formatMessage({ id: 'table.rowsPerPage', defaultMessage: 'Rows per page' })}</span>
                  <select
                    value={String(pageSize)}
                    onChange={(e) => onPageSizeChange(e.target.value)}
                    style={{
                      padding: '5px 10px',
                      borderRadius: 'var(--r-sm)',
                      border: '1px solid var(--line)',
                      background: 'var(--surface)',
                      fontFamily: 'inherit',
                      fontSize: 'var(--t-sm)',
                      color: 'var(--ink)',
                      cursor: 'pointer',
                    }}
                  >
                    {pageSizeSelectOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </>
              )}
              <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
                <Pagination
                  forcePage={currentPage - 1}
                  {...{ pageCount }}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                  onPageChange={({ selected }) => onPageChange(selected + 1)}
                />
              </div>
            </div>
          </div>
        )}
    </>
  );
}

export default Table;
